import { Box } from "@mui/material"
import { Outlet, getRouteApi } from "@tanstack/react-router"
import type { FC } from "react"
import { useMemo } from "react"

import { PENDING_APPROVALS_ROUTE, TaskViewScope } from "../constants"
import { PendingApprovalsProvider } from "../contexts"
import { useGetDashboardTaskCounts } from "../hooks/useGetDashboardTaskCounts"

import { ApprovalBucketTabs } from "./ApprovalBucketTabs"
import { DashboardGreeting } from "./DashboardGreeting"

import PermissionDeniedAlert from "@/components/common/Alerts/PermissionDeniedAlert"
import { AerosSelect } from "@/components/UiKit"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useCurrentUserStore } from "@/stores/currentUserStore"
import type { Option } from "@/types/form-fields"

const routeApi = getRouteApi(PENDING_APPROVALS_ROUTE)

export const PendingApprovalsPage: FC = () => {
  const currentUser = useCurrentUserStore((state) => state.currentUser)
  const { hasPermissions } = usePermissionManager()
  const navigate = routeApi.useNavigate()
  const { taskViewScope } = routeApi.useSearch()

  const canViewAllTasks = hasPermissions([PermissionActionType.DASHBOARD_OTHER_USER_TASK_MAINTENANCE])

  const taskViewScopeOptions = useMemo<Option<TaskViewScope>[]>(
    () => [
      { value: TaskViewScope.MyTasks, label: "My Tasks" },
      ...(canViewAllTasks ? [{ value: TaskViewScope.AllTasks, label: "All Tasks" }] : []),
    ],
    [canViewAllTasks]
  )

  const assignedToId = taskViewScope === TaskViewScope.MyTasks || !canViewAllTasks ? currentUser?.userId : undefined

  const { taskCounts, isFetching, refetch: refetchTaskCounts } = useGetDashboardTaskCounts({ assignedToId })

  const handleTaskViewScopeChange = (value: TaskViewScope | null) => {
    navigate({
      search: (prev) => ({ ...prev, taskViewScope: value ?? TaskViewScope.MyTasks }),
      replace: true,
    })
  }

  if (!currentUser) return null

  if (taskViewScope === TaskViewScope.AllTasks && !canViewAllTasks) return <PermissionDeniedAlert />

  return (
    <Box className='flex min-h-0 flex-1 flex-col gap-y-6' data-testid='dashboard.pending-approvals'>
      <Box className='flex items-end justify-between gap-x-4'>
        <DashboardGreeting message={`You have ${taskCounts.total} tasks to review today`} />
        <AerosSelect<TaskViewScope>
          label='View Tasks By'
          options={taskViewScopeOptions}
          value={taskViewScope}
          onChange={handleTaskViewScopeChange}
          size='small'
          className='w-[240px]'
          data-testid='dashboard.pending-approvals.view-tasks-by-select'
        />
      </Box>
      <ApprovalBucketTabs taskCounts={taskCounts} loading={isFetching} />
      <PendingApprovalsProvider refetchTaskCounts={refetchTaskCounts}>
        <Outlet />
      </PendingApprovalsProvider>
    </Box>
  )
}
